import React, { useState, useMemo, useCallback } from 'react';
import { useData } from '../../context/DataContext';
import { useLanguage } from '../../context/LanguageContext';
import { Home, Grid, Map as MapIcon, X } from 'lucide-react';

import HideoutCard from './HideoutCard';
import HideoutMap from './HideoutMap';

const MODULE_POSITIONS = {
  'Vents': { x: 53, y: 36 }, 'Security': { x: 33, y: 50.5 }, 'Lavatory': { x: 44, y: 18 },
  'Stash': { x: 44, y: 30 }, 'Generator': { x: 53, y: 41 }, 'Heating': { x: 74, y: 41 },
  'Water Collector': { x: 58, y: 33 }, 'Medstation': { x: 68, y: 32 }, 'Nutrition Unit': { x: 58, y: 41 },
  'Rest Space': { x: 76, y: 32 }, 'Workbench': { x: 50, y: 14 }, 'Intelligence Center': { x: 50, y: 25 },
  'Shooting Range': { x: 46, y: 6 }, 'Library': { x: 77, y: 37 }, 'Scav Case': { x: 41, y: 45 },
  'Illumination': { x: 48, y: 35 }, 'Air Filtering Unit': { x: 67, y: 41 }, 'Solar Power': { x: 46, y: 41 },
  'Booze Generator': { x: 62, y: 32 }, 'Bitcoin Farm': { x: 50, y: 30 }, 'Christmas Tree': { x: 55, y: 48 },
  'Defective Wall': { x: 65, y: 29 }, 'Gym': { x: 73, y: 9 }, 'Weapon Rack': { x: 46, y: 13 },
  'Hall of Fame': { x: 58, y: 9 }, 'Gear Rack': { x: 59, y: 21 }, 'Cultist Circle': { x: 31, y: 78 }
};

function Hideout({ profile, onUpdate }) {
  const { hideout: hideoutModules, isLoaded } = useData();
  const { t } = useLanguage();

  const [hideoutLevels, setHideoutLevels] = useState(profile?.hideoutModules || {});
  const [filter, setFilter] = useState('available');
  const [viewMode, setViewMode] = useState('map');
  const [selectedModuleId, setSelectedModuleId] = useState(null);

  const canUpgrade = useCallback((module, targetLevel, currentLevels) => {
    const currentLevel = currentLevels[module.id] || 0;
    if (currentLevel >= targetLevel || targetLevel !== currentLevel + 1) return false;
    const levelData = module.levels.find(l => l.level === targetLevel);
    if (!levelData) return false;
    if (levelData.stationLevelRequirements) {
      for (const req of levelData.stationLevelRequirements) {
        const requiredStation = hideoutModules.find(m => m.id === req.station.id);
        if (!requiredStation) continue;
        if ((currentLevels[requiredStation.id] || 0) < req.level) return false;
      }
    }
    return true;
  }, [hideoutModules]);

  const handleUpgrade = (moduleId, targetLevel) => {
    const newLevels = { ...hideoutLevels, [moduleId]: targetLevel };
    setHideoutLevels(newLevels);
    const updatedProfile = { ...(profile || {}), hideoutModules: newLevels };
    localStorage.setItem('tarkovProfile', JSON.stringify(updatedProfile));
    onUpdate(updatedProfile);
  };

  const filteredModules = useMemo(() => {
    if (!hideoutModules) return [];
    if (filter === 'all') return hideoutModules;
    return hideoutModules.filter(m => {
      const currentLevel = hideoutLevels[m.id] || 0;
      if (filter === 'available') return currentLevel < m.levels.length && canUpgrade(m, currentLevel + 1, hideoutLevels);
      if (filter === 'locked') return currentLevel < m.levels.length && !canUpgrade(m, currentLevel + 1, hideoutLevels);
      if (filter === 'maxed') return currentLevel >= m.levels.length;
      return true;
    });
  }, [filter, hideoutModules, hideoutLevels, canUpgrade]);

  if (!isLoaded) return <div className="h-screen bg-black flex items-center justify-center text-emerald-500 font-mono animate-pulse">{t('loading_hideout') || 'Loading...'}</div>;

  return (
    // YENİ HALİ: select-none eklendi!
    <div className="h-[calc(100vh-64px)] w-full bg-black relative overflow-hidden flex flex-col select-none">
      {/* Navbar Section */}
      <div className={`absolute top-0 left-0 right-0 z-40 p-4 pointer-events-none ${viewMode === 'list' ? 'relative bg-transparent p-6 pointer-events-auto' : ''}`}>
        <div className="flex justify-between items-center pointer-events-auto max-w-[1920px] mx-auto w-full">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4 bg-[#09090b]/95 backdrop-blur border border-white/5 p-2 rounded-sm shadow-xl">
              <div className="w-10 h-10 bg-zinc-900 rounded-sm flex items-center justify-center border border-zinc-800"><Home className="text-emerald-500 w-5 h-5" /></div>
              <div><h1 className="text-lg font-bold text-white uppercase tracking-wider leading-none">{t('hideout') || 'Hideout'}</h1><p className="text-[10px] text-zinc-500 font-mono uppercase">Interactive Base</p></div>
            </div>
            <div className="flex bg-[#09090b]/95 backdrop-blur border border-white/5 p-1 rounded-sm shadow-xl">
              <button onClick={() => setViewMode('map')} className={`p-2 rounded-sm transition-all ${viewMode === 'map' ? 'bg-emerald-900/30 text-emerald-400' : 'text-zinc-500'}`}><MapIcon className="w-5 h-5" /></button>
              <button onClick={() => setViewMode('list')} className={`p-2 rounded-sm transition-all ${viewMode === 'list' ? 'bg-emerald-900/30 text-emerald-400' : 'text-zinc-500'}`}><Grid className="w-5 h-5" /></button>
            </div>
          </div>
          {viewMode === 'list' && (
            <div className="flex gap-1 bg-black p-1 rounded-sm border border-zinc-800">
              {['available', 'all', 'locked', 'maxed'].map(f => (
                <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-sm text-[10px] font-bold uppercase tracking-wide transition-all ${filter === f ? 'bg-zinc-800 text-white' : 'text-zinc-500'}`}>{t(`hideout_${f}`) || f}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      {viewMode === 'map' ? (
        <HideoutMap
          hideoutModules={hideoutModules}
          hideoutLevels={hideoutLevels}
          selectedModuleId={selectedModuleId}
          setSelectedModuleId={setSelectedModuleId}
          canUpgrade={canUpgrade}
          t={t}
          modulePositions={MODULE_POSITIONS}
        />
      ) : (
        <div className="p-8 overflow-y-auto h-full max-w-[1920px] mx-auto w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredModules.map(module => (
              <HideoutCard
                key={module.id}
                module={module}
                hideoutLevels={hideoutLevels}
                hideoutModules={hideoutModules}
                canUpgrade={canUpgrade}
                handleUpgrade={handleUpgrade}
              />
            ))}
          </div>
        </div>
      )}

      {/* Sidebar Panel for Map */}
      {viewMode === 'map' && selectedModuleId && (
        <div className="absolute top-0 right-0 h-full w-96 bg-[#09090b]/95 backdrop-blur-md border-l border-zinc-800 p-6 z-50 shadow-2xl animate-slideInRight flex flex-col">
          <button onClick={() => setSelectedModuleId(null)} className="ml-auto text-zinc-500 hover:text-red-500 p-2 rounded-md transition-colors hover:bg-zinc-800"><X className="w-6 h-6" /></button>
          <div className="flex-1 overflow-y-auto custom-scrollbar select-text pr-2 mt-4">
            <HideoutCard
              module={hideoutModules.find(m => m.id === selectedModuleId)}
              hideoutLevels={hideoutLevels}
              hideoutModules={hideoutModules}
              canUpgrade={canUpgrade}
              handleUpgrade={handleUpgrade}
            />
          </div>
        </div>
      )}

      <style>{`
        .clip-hexagon { clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%); }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slideInRight { animation: slideInRight 0.3s ease-out; }
      `}</style>
    </div>
  );
}

export default Hideout;